import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faArrowLeft, faSpinner, faTimes } from '@fortawesome/free-solid-svg-icons';
import { createDespesa, fetchContasDespesa } from '../../../services/despesaService';
import { formatBRL } from '../../../utils/currency';
import ConfirmModal from '../../../components/ConfirmModal';

const DespesaCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [contasOptions, setContasOptions] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingData, setPendingData] = useState(null);
  const [formData, setFormData] = useState({
    descricao: '',
    numeroNF: '',
    valor: '',
    dataBalanco: '',
    conta: ''
  });
  const [errors, setErrors] = useState({});

  // Carregar opções para dropdowns
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const contas = await fetchContasDespesa();
        // Filtrar apenas contas do tipo 'Saida' quando possível
        const filtered = (contas || []).filter(c => {
          if (!c) return false;
          if (typeof c === 'object') {
            const tipo = (c.tipo || c.natureza || c.type || '').toString().toLowerCase();
            if (tipo) return tipo === 'saida' || tipo === 'saída';
            const desc = (c.descricao || c.label || c.nome || '').toString().toLowerCase();
            return desc.includes('saida') || desc.includes('saída');
          }
          const s = String(c).toLowerCase();
          return s.includes('saida') || s.includes('saída');
        });
        setContasOptions(filtered);
      } catch (err) {
        console.error('Erro ao carregar opções:', err);
      }
    };
    
    loadOptions();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.descricao.trim()) {
      newErrors.descricao = 'Descrição é obrigatória';
    }

    if (!formData.valor) {
      newErrors.valor = 'Valor é obrigatório';
    } else if (parseFloat(formData.valor) <= 0) {
      newErrors.valor = 'Valor deve ser maior que zero';
    }

    if (!formData.dataBalanco) {
      newErrors.dataBalanco = 'Data do balanço é obrigatória';
    }

    if (!formData.conta.trim()) {
      newErrors.conta = 'Conta é obrigatória';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const despesaData = {
      ...formData,
      valor: parseFloat(formData.valor)
    };

    // converter conta para número se for numérico
    if (!isNaN(Number(formData.conta))) {
      despesaData.conta = Number(formData.conta);
    }

    setPendingData(despesaData);
    setShowConfirmModal(true);
  };

  const handleConfirmSave = async () => {
    setLoading(true);
    setShowConfirmModal(false);
    
    try {
      await createDespesa(pendingData);
      navigate('/despesas', { 
        state: { 
          message: 'Despesa criada com sucesso!', 
          type: 'success' 
        } 
      });
    } catch (err) {
      setErrors({ 
        submit: err.message || 'Erro ao criar despesa' 
      });
    } finally {
      setLoading(false);
      setPendingData(null);
    }
  };

  const handleCancelSave = () => {
    setShowConfirmModal(false);
    setPendingData(null);
  };

  const handleCancel = () => {
    navigate('/despesas');
  };

  return (
    <div className="w-full bg-white rounded shadow p-6 mt-6">
      <div className="flex items-center mb-4">
        <button
          onClick={handleCancel}
          className="mr-4 text-gray-600 hover:text-gray-800 transition-colors"
        >
          <FontAwesomeIcon icon={faArrowLeft} size="lg" />
        </button>
        <h2 className="text-xl font-bold">Cadastrar Nova Despesa</h2>
      </div>
      {/* `noValidate`: a validação em português deste formulário é a que o
          usuário deve ver — o balão nativo do navegador some sozinho, sai no
          idioma do navegador e mostra um campo por vez. */}


      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        {errors.submit && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {errors.submit}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label htmlFor="descricao" className="block text-sm font-medium mb-1">
              Descrição *
            </label>
            <input
              type="text"
              id="descricao"
              value={formData.descricao}
              onChange={(e) => handleInputChange('descricao', e.target.value)}
              className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                errors.descricao ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Ex: Material de Escritório"
            />
            {errors.descricao && (
              <p className="mt-1 text-sm text-red-600">{errors.descricao}</p>
            )}
          </div>

          <div>
            <label htmlFor="numeroNF" className="block text-sm font-medium mb-1">
              Número da Nota Fiscal
            </label>
            <input
              type="text"
              id="numeroNF"
              value={formData.numeroNF}
              onChange={(e) => handleInputChange('numeroNF', e.target.value)}
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Ex: NF-001"
            />
          </div>

          <div>
            <label htmlFor="valor" className="block text-sm font-medium mb-1">
              Valor *
            </label>
            <input
              type="number"
              id="valor"
              step="0.01"
              min="0"
              value={formData.valor}
              onChange={(e) => handleInputChange('valor', e.target.value)}
              className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                errors.valor ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="0,00"
            />
            {errors.valor && (
              <p className="mt-1 text-sm text-red-600">{errors.valor}</p>
            )}
          </div>

          <div>
            <label htmlFor="dataBalanco" className="block text-sm font-medium mb-1">
              Data do Balanço *
            </label>
            <input
              type="date"
              id="dataBalanco"
              value={formData.dataBalanco}
              onChange={(e) => handleInputChange('dataBalanco', e.target.value)}
              className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                errors.dataBalanco ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.dataBalanco && (
              <p className="mt-1 text-sm text-red-600">{errors.dataBalanco}</p>
            )}
          </div>

          <div className="md:col-span-2">
            <label htmlFor="conta" className="block text-sm font-medium mb-1">
              Conta *
            </label>
            <div className="relative">
              <select
                id="conta"
                value={formData.conta}
                onChange={(e) => handleInputChange('conta', e.target.value)}
                className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                  errors.conta ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Selecione uma conta</option>
                {contasOptions.map((c, index) => {
                  if (!c) return null;
                  if (typeof c === 'object') {
                    const val = c.id != null ? String(c.id) : (c.descricao || c.label || c.nome || `opt-${index}`);
                    const label = c.descricao || c.label || c.nome || val;
                    return <option key={val} value={val}>{label}</option>;
                  }
                  const sval = String(c);
                  return <option key={`${sval}-${index}`} value={sval}>{sval}</option>;
                })}
              </select>
            </div>
            {errors.conta && (
              <p className="mt-1 text-sm text-red-600">{errors.conta}</p>
            )}
          </div>
        </div>

        <div className="flex flex-row gap-1 justify-end mt-2 w-full overflow-x-auto">
          <button
            type="button"
            onClick={handleCancel}
            className="p-2 sm:px-4 sm:py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 flex items-center gap-2 w-10 sm:w-auto justify-center"
            disabled={loading}
           aria-label="Cancelar">
            <FontAwesomeIcon icon={faTimes} />
            <span className="hidden sm:inline">Cancelar</span>
          </button>
          <button
            type="submit"
            disabled={loading}
            className="p-2 sm:px-4 sm:py-2 rounded bg-red-600 text-white font-semibold hover:bg-red-700 flex items-center gap-2 w-10 sm:w-auto justify-center disabled:opacity-50 disabled:cursor-not-allowed"
           aria-label="Salvar">
            {loading ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <FontAwesomeIcon icon={faSave} />
            )}
            <span className="hidden sm:inline">
              {loading ? 'Salvando...' : 'Salvar'}
            </span>
          </button>
        </div>
      </form>

      <ConfirmModal
        isOpen={showConfirmModal}
        title="Confirmar Criação"
    message={`Deseja realmente criar a despesa "${formData.descricao}" no valor de ${formatBRL(formData.valor || 0)}?`}
        onConfirm={handleConfirmSave}
        onCancel={handleCancelSave}
        danger={false}
      />
    </div>
  );
};

export default DespesaCreate;
